import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { schedulerApi } from './schedulerApi';

export const portfolioApi = createApi({
  reducerPath: 'portfolioApi',
  baseQuery,
  tagTypes: ['UserList', 'PortfolioList', 'Portfolio'],
  endpoints: (builder) => ({
    getPortfolios: builder.query({
      query: ({
        page = 1,
        size = 10,
        sortBy = 'portfolioName',
        sortDir = 'asc',
        search = '',
      } = {}) => {
        const params = new URLSearchParams({ page, size, sortBy, sortDir });
        if (search) params.set('search', search);
        return `/portfolios/getAllPortfolios?${params}`;
      },
      transformResponse: (response) => {
        const items = response.result || response || [];
        return {
          data: items,
          pagination: response.pagination || null,
        };
      },
      providesTags: ['PortfolioList'],
    }),

    getPortfolio: builder.query({
      query: (portfolioId) => `/portfolios/${portfolioId}`,
      transformResponse: (response) => response.result || response,
      providesTags: (result, error, portfolioId) => [{ type: 'Portfolio', id: portfolioId }],
    }),

    createPortfolio: builder.mutation({
      query: (portfolio) => ({
        url: '/portfolios',
        method: 'POST',
        body: portfolio,
      }),
      invalidatesTags: ['PortfolioList'],
    }),

    updatePortfolio: builder.mutation({
      query: ({ portfolioId, ...data }) => ({
        url: `/portfolios/${portfolioId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { portfolioId }) => [
        { type: 'Portfolio', id: portfolioId },
        'PortfolioList',
      ],
    }),

    updatePortfolioSettings: builder.mutation({
      query: ({ portfolioId, ...settings }) => ({
        url: `/portfolios/${portfolioId}/settings`,
        method: 'PUT',
        body: settings,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(schedulerApi.util.invalidateTags(['SchedulerStatus']));
        } catch {
          // Ignore invalidation if the settings request fails.
        }
      },
      invalidatesTags: (result, error, { portfolioId }) => [
        { type: 'Portfolio', id: portfolioId },
        'PortfolioList',
      ],
    }),

    getPortfolioTags: builder.query({
      query: (portfolioId) => `/portfolios/${portfolioId}/tags`,
      transformResponse: (response) => {
        const data = response.result || response || {};
        return data.tags || [];
      },
      providesTags: (result, error, portfolioId) => [{ type: 'Portfolio', id: portfolioId }],
    }),

    getPortfolioUsers: builder.query({
      query: ({
        portfolioId,
        page = 1,
        size = 10,
        sortBy = 'fullName',
        sortDir = 'asc',
        search = '',
      }) => {
        const params = new URLSearchParams({ page, size, sortBy, sortDir });
        if (search) params.set('search', search);
        return `/users/portfolio/${portfolioId}?${params}`;
      },
      transformResponse: (response) => {
        const items = response.result || response || [];
        return {
          data: items,
          pagination: response.pagination || null,
        };
      },
      providesTags: (_result, _error, { portfolioId }) => [
        { type: 'UserList', id: portfolioId },
        'UserList',
      ],
    }),

    inviteUser: builder.mutation({
      query: ({ email, firstName, lastName, role, portfolioId }) => ({
        url: '/invite/createInvite',
        method: 'POST',
        body: {
          email,
          firstName,
          lastName,
          role: role,
          portfolioId,
        },
      }),
      invalidatesTags: ['UserList', 'PortfolioList'],
    }),

    createUser: builder.mutation({
      query: ({ email, firstName, lastName, role }) => ({
        url: '/users/createUser',
        method: 'POST',
        body: {
          email,
          firstName,
          lastName,
          role,
        },
      }),
      invalidatesTags: ['UserList', 'PortfolioList'],
    }),

    updateUser: builder.mutation({
      query: ({ userId, ...data }) => ({
        url: `/users/updateUser/${userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['UserList', 'PortfolioList'],
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserList', 'PortfolioList'],
    }),

    syncSparefootBids: builder.mutation({
      query: (portfolioId) => ({
        url: `/sparefoot/syncBids/${portfolioId}`,
        method: 'GET',
      }),
    }),

    getSchedulerRunHistory: builder.query({
      query: (portfolioId) => `/scheduler/getRunHistory/${portfolioId}`,
      transformResponse: (response) => response.result || response || [],
    }),
  }),
});

export const {
  useGetPortfoliosQuery,
  useGetPortfolioQuery,
  useCreatePortfolioMutation,
  useUpdatePortfolioMutation,
  useUpdatePortfolioSettingsMutation,
  useGetPortfolioTagsQuery,
  useGetPortfolioUsersQuery,
  useInviteUserMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useSyncSparefootBidsMutation,
  useGetSchedulerRunHistoryQuery,
} = portfolioApi;
